import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const EsaHours = () => {
  const navigate = useNavigate();
  const itemId = "esa-hours";
  const fieldOrder = useMemo(() => ["agreement", "date", "employeeName", "employeeSignature"], []);
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, [fieldOrder]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  // Centralized validation used by both live status updates and the submit handler
  const validateForm = (data) => {
    const errors = {};
    if (!data.agreement) errors.agreement = "Please choose one option";
    if (!data.date) errors.date = "This field is required";
    else if (!isDateToday(data.date)) errors.date = "Date must be today";
    if (!String(data.employeeName || "").trim()) errors.employeeName = "This field is required";
    if (!data.employeeSignature) errors.employeeSignature = "Signature is required";

    const values = fieldOrder.map((f) => (data[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const dateValid = !errors.date;
    const agreementValid = !errors.agreement;
    const signatureValid = !errors.employeeSignature;

    const status = allEmpty
      ? "incomplete"
      : allFilled && dateValid && agreementValid && signatureValid
      ? "complete"
      : "in-progress";

    return { errors, status };
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));

    const { errors, status: newStatus } = validateForm(formData);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const payload = { status: newStatus, formData };
    if (accountId && newStatus === "complete") {
      axios
        .post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch((err) => console.error("Failed to save item:", err))
        .finally(() => {
          axios
            .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} })
            .catch(() => {});
          localStorage.setItem(`status_${accountId}_${itemId}`, newStatus);
          showSuccess("Form completed successfully!");
          navigate("/register");
        });
    } else {
      if (accountId)
        axios
          .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} })
          .catch(() => {});
      localStorage.setItem(`status_${accountId}_${itemId}`, newStatus);
      navigate("/register");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (field === "date") {
        if (value && isDateToday(value)) delete next[field];
      } else if (field === "agreement") {
        if (["accept", "decline"].includes(String(value))) delete next[field];
      } else {
        if (String(value || "").trim()) delete next[field];
      }
      return next;
    });
  };

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const { status } = validateForm(formData);
    const statusToPersist = status === "complete" ? (existing === "complete" ? "complete" : "in-progress") : status;
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData]);

  const handleClearAll = () => {
    setFormData(fieldOrder.reduce((acc, f) => { acc[f] = ""; return acc; }, {}));
  };

  const handleBack = () => {
    navigate("/register");
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
            <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
                <h2 className="form-title" style={{ textAlign: "center" }}>
                  ESA Excess Hours Agreement
                </h2>
          </header>

          <section className="form-card-content">
            {err && <div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>}
            <p>
              This agreement specifies the Contract Employee’s decision to exceed the Ontario Employment Standard Act,
            </p>
            <p>
              Hours of Work provisions,
            </p>
            <p className="form-intro">
              WHEREAS the agreeing parties,
            </p>
            <p>
              AND
            </p>
            <p style={{ textDecoration: "underline" }}>
                Talent Employment Inc.
            </p>
            <p className="form-intro">
              Herein referred to as “Talent”
            </p>
            <p className="form-intro">
              Have considered the terms and conditions of this agreement as follows:
            </p>
            <ul className="form-bullets topbullets">
              <li>1. The Contract Employee may work in excess of 8 hours per day and/or 48 hours per week, as requested by their direct supervisor but will not exceed more than 12 hours per day and/or 72 hours per week. The Contract Employee acknowledges that they may choose not to work over 8 hours per day and/or 48 hours per week, and accordingly, may decline the terms of this agreement.</li>
              <li>2. The Contract Employee is in receipt of the most recent copy of Ontario Ministry of Labour’s Information for the Employees About Hours of Work and Overtime Pay, and understands the provisions explained therein.</li>
              <li>3. The Contract Employee may terminate this Agreement at any time by providing two (2) weeks written notice. Talent can revoke this agreement to work excess daily or weekly hours by giving the Contract Employee reasonable notice.</li>
              <li>4. This Agreement is in effect upon execution, with signature acknowledgement. It will expire on the date indicated on the Ministry of Labour’s most recent and applicable approval.</li>
              <li>5. The Contract Employee is aptly informed in order to accept or decline the terms of the Agreement, and may do so voluntarily.</li>
            </ul>
            <p style={{ fontSize: "12px" }}>Please Choose*</p>
            <label className="option">
                <input type="radio" name="agreement" value="accept" checked={formData.agreement === "accept"} onChange={(e) => handleInputChange("agreement", e.target.value)} />
                <span>I hereby accept the terms of this Agreement and wish to be able to work in excess of 8 hours per day and/or 48 hours per week, but up to a maximum of 72 weekly hours.</span>
            </label>

            <label className="option">
                <input type="radio" name="agreement" value="decline" checked={formData.agreement === "decline"} onChange={(e) => handleInputChange("agreement", e.target.value)} />
                <span>I hereby DECLINE the terms of this Agreement; I do not want to work excess hours.</span>
            </label>
            {fieldErrors.agreement && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.agreement}</div>}
            <div className="grid-2">
              <div className="form-field">
                <label htmlFor="date">Date*</label>
                <DateInput
                  id="date"
                  value={formData.date}
                  onChange={(v) => handleInputChange("date", v)}
                  required
                />
                {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
              </div>

              <div className="form-field">
                <label htmlFor="employeeName">Employee's Full Name*</label>
                <input
                  id="employeeName"
                  type="text"
                  value={formData.employeeName}
                  onChange={(e) =>
                    handleInputChange("employeeName", e.target.value)
                  }
                  required
                />
                {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
              </div>
            </div>
          </section>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            {/* <label>Employee's Signature*</label> */}
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EsaHours;
